import { useCallback, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import {
  basketNumPortionChange,
  basketRecipePositionsInitialise,
  basketRecipesInitialise,
} from 'actions/basket'
import type { PortionSizes } from 'config/portionSizes'
import { getBasketRecipesCount, getNumPortions } from 'selectors/basket'

import { trackEditOrderDetailsBoxSizeCompleted } from './tracking'
import { DEFAULT_PORTION_SIZE } from './useDeliverySettings'

export type UseEditBoxSize = {
  selectedSize: PortionSizes
  setSelectedSize: (size: PortionSizes) => void
  basketWillBeEmptied: boolean
  menuWillBeUpdated: boolean
  isConfirmDisabled: boolean
  confirm: () => void
}

export const useEditBoxSize = (): UseEditBoxSize => {
  const dispatch = useDispatch()
  // getNumPortions is typed PortionSizes but Immutable may return undefined.
  const currentSize: PortionSizes =
    (useSelector(getNumPortions) as PortionSizes | undefined) ?? DEFAULT_PORTION_SIZE
  const basketRecipesCount = useSelector(getBasketRecipesCount)

  const [selectedSize, setSelectedSizeState] = useState<PortionSizes>(currentSize)

  const setSelectedSize = useCallback((size: PortionSizes) => {
    setSelectedSizeState(size)
  }, [])

  const hasChanged = selectedSize !== currentSize

  const confirm = useCallback(() => {
    if (selectedSize === currentSize) return

    if (basketRecipesCount > 0) {
      dispatch(basketRecipesInitialise({}))
      dispatch(basketRecipePositionsInitialise([]))
    }
    // TODO: confirm view value with analytics
    dispatch(basketNumPortionChange(selectedSize, 'gousto_anytime_experiment'))
    trackEditOrderDetailsBoxSizeCompleted({
      previous_num_portions: currentSize,
      updated_num_portions: selectedSize,
    })
  }, [dispatch, selectedSize, currentSize, basketRecipesCount])

  return {
    selectedSize,
    setSelectedSize,
    basketWillBeEmptied: hasChanged && basketRecipesCount > 0,
    menuWillBeUpdated: hasChanged && basketRecipesCount === 0,
    isConfirmDisabled: !hasChanged,
    confirm,
  }
}
